import { makeRequest } from "@/makeRequest";
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from "@simplewebauthn/browser";


import { AUTH_ENDPOINTS } from "@vehidhi/shared";

export function supportsPasskeys() {
  return browserSupportsWebAuthn();
}

/**
 * =========================
 * REGISTER PASSKEY
 * =========================
 */
export async function registerPasskey() {
  // 1. Get options from backend
  const options = await makeRequest(
    AUTH_ENDPOINTS.PASSKEY_REGISTER_OPTIONS.path,
    {
      method: AUTH_ENDPOINTS.PASSKEY_REGISTER_OPTIONS.method,
      auth: true,
    }
  );

  if (!options) {
    throw new Error("Failed to get registration options");
  }

  // 2. Browser creates credential (FaceID / TouchID / etc)
  const registrationResponse =
    await startRegistration(options);

  // 3. Send to backend for verification
  const result = await makeRequest(
    AUTH_ENDPOINTS.PASSKEY_REGISTER_VERIFY.path,
    {
      method: AUTH_ENDPOINTS.PASSKEY_REGISTER_VERIFY.method,
      body: registrationResponse,
      auth: true,
    }
  );

  return result;
}

/**
 * =========================
 * LOGIN WITH PASSKEY
 * =========================
 */
export async function loginWithPasskey(email: string) {
  // 1. Get login options
  const options = await makeRequest(
    AUTH_ENDPOINTS.PASSKEY_LOGIN_OPTIONS.path,
    {
      method: AUTH_ENDPOINTS.PASSKEY_LOGIN_OPTIONS.method,
      body: { email },
      auth: false,
    }
  );

  if (!options) {
    throw new Error("Failed to get login options");
  }

  // 2. User authenticates via device
  const authResponse =
    await startAuthentication(options);

  // 3. Verify on backend
  const result = await makeRequest(
    AUTH_ENDPOINTS.PASSKEY_LOGIN_VERIFY.path,
    {
      method: AUTH_ENDPOINTS.PASSKEY_LOGIN_VERIFY.method,
      body: authResponse,
      auth: false,
    }
  );

  return result;
}

/**
 * =========================
 * CONDITIONAL PASSKEY LOGIN (Google-style autofill)
 * =========================
 */
export async function loginWithConditionalPasskey(email?: string) {
  try {
    if (!supportsPasskeys()) return null;

    const options = await makeRequest(
      AUTH_ENDPOINTS.PASSKEY_LOGIN_OPTIONS.path,
      {
        method: AUTH_ENDPOINTS.PASSKEY_LOGIN_OPTIONS.method,
        body: email ? { email } : {},
        auth: false,
      }
    );

    const authResponse = await startAuthentication(
      options,
      true // 👈 conditional UI
    );

    const result = await makeRequest(
      AUTH_ENDPOINTS.PASSKEY_LOGIN_VERIFY.path,
      {
        method: AUTH_ENDPOINTS.PASSKEY_LOGIN_VERIFY.method,
        body: authResponse,
        auth: false,
      }
    );

    return result;
  } catch (err) {
    console.warn("Conditional passkey failed:", err);
    return null;
  }
}